/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/**
 * Adds your navigation menu items to the given itemsMap.
 * This function is called last after adding navigation menu items to the itemsMap from all other installed addons.
 *
 * @param itemsMap A Map containing navigation menu items. This object is a Map that has route paths (urls) as
 * keys and menu item object with the following shape
 *
 *   {
 *    title: STRING, // Title for the navigation menu item
 *    icon: STRING, // semantic ui icon name fot the navigation menu item
 *    shouldShow: FUNCTION, // A function that returns a flag indicating whether to show the item or not (useful when showing menu items conditionally)
 *    render: OPTIONAL FUNCTION, // Optional function that returns rendered menu item component. Use this ONLY if you want to control full rendering of the menu item.
 *   }
 *
 * @param appContext An application context object containing all MobX store objects
 *
 * @returns Map<*> Returns A Map containing navigation menu items with the same shape as "itemsMap"
 */

import _ from 'lodash';

// eslint-disable-next-line no-unused-vars
function registerMenuItems(itemsMap, { location, appContext }) {
  const isAdmin = _.get(appContext, 'userStore.user.isAdmin');

  // Collect items that should be grouped under "Configuration"
  const configUrls = ['/configuration/accounts', '/announcements', '/workspace-types-management', '/data-sources', '/workflows/published'];
  const configChildren = [];

  // Pull matching items out of the flat map and into the Configuration group
  for (const url of configUrls) {
    if (itemsMap.has(url)) {
      const item = itemsMap.get(url);
      configChildren.push({ url, ...item });
      itemsMap.delete(url);
    }
  }

  // Add the Accounts page (Indexes + AWS Accounts) as a Configuration child
  configChildren.unshift({
    url: '/configuration/accounts',
    title: 'Accounts',
    icon: 'amazon',
    shouldShow: isAdmin,
    adminOnly: true,
  });

  const configItem = {
    title: 'Configuration',
    icon: 'cog',
    shouldShow: isAdmin,
    adminOnly: true,
    children: configChildren,
  };

  // Rebuild the map in the desired display order
  const orderedKeys = [
    '/dashboard',
    '__configuration',
    '/users',
    '/projects',
    '/key-pair-management',
    '/studies',
    '/workspaces',
    '/documentation',
  ];

  const ordered = new Map();
  for (const key of orderedKeys) {
    if (key === '__configuration') {
      ordered.set(key, configItem);
    } else if (itemsMap.has(key)) {
      ordered.set(key, itemsMap.get(key));
    }
  }

  // Append any remaining items not in the explicit order
  for (const [key, value] of itemsMap) {
    if (!ordered.has(key)) {
      ordered.set(key, value);
    }
  }

  return ordered;
}

const plugin = {
  registerMenuItems,
};

export default plugin;
